/**
 * DateEditorWrapperV16 - Custom date editor with Pikaday for Handsontable v16
 *
 * Creates its own input field and Pikaday calendar
 */
import type Handsontable from 'handsontable-v16';
import { type BaseEditor, TextEditor } from 'handsontable-v16/editors';
import { registerCellType } from 'handsontable-v16/cellTypes';
import type { CellProperties } from 'handsontable-v16/settings';
import { DateUtils } from '@/utils/Date.utils';
import { _isEmpty, _isNil, _notNil } from '@/littledash';

// Import Pikaday
import Pikaday from 'pikaday';
import 'pikaday/css/pikaday.css';

interface DropdownIconMousedownHandlerV16Props {
  hotInstance: Handsontable.Core & {
    dropdownArrowListenerV16?: (event: Event) => void;
  };
  row: number;
  column: number;
}

/**
 * Adds single click support to the cell dropdown arrow to open the date picker
 */
const addDropdownIconMousedownHandlerV16 = ({ hotInstance, row, column }: DropdownIconMousedownHandlerV16Props) => {
  if (_isNil(hotInstance.dropdownArrowListenerV16)) {
    hotInstance.dropdownArrowListenerV16 = (event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('dateDropdownArrowV16')) {
        event.stopImmediatePropagation();

        const rowAttr = target.getAttribute('data-row');
        const colAttr = target.getAttribute('data-col');
        const targetRow = _isNil(rowAttr) ? row : parseInt(rowAttr, 10);
        const targetCol = _isNil(colAttr) ? column : parseInt(colAttr, 10);

        setTimeout(() => {
          hotInstance.selectCell(targetRow, targetCol);
          const editor = hotInstance.getActiveEditor();
          if (_notNil(editor) && (editor.isOpened() ?? false) === false) {
            editor.beginEditing();
          }
        }, 0);
      }
    };

    hotInstance.rootElement.addEventListener('mousedown', hotInstance.dropdownArrowListenerV16, true);
    hotInstance.addHookOnce('afterDestroy', () => {
      if (_notNil(hotInstance.dropdownArrowListenerV16)) {
        hotInstance.rootElement.removeEventListener('mousedown', hotInstance.dropdownArrowListenerV16, true);
      }
    });
  }
};

/**
 * Custom renderer for date cells with dropdown arrow v16
 */
export const dateRendererV16 = (
  hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  row: number,
  column: number,
  _prop: string | number,
  value: unknown,
  _cellProperties: CellProperties
) => {
  td.innerHTML = '';

  // Create date display
  const dateContainer = document.createElement('div');
  dateContainer.innerText = DateUtils.renderDate(value as string, { defaultResponse: (value as string) ?? '' });
  dateContainer.style.paddingRight = '20px'; // Space for arrow

  // Create dropdown arrow icon with data attributes
  const iconContainer = document.createElement('div');
  iconContainer.classList.add('htAutocompleteArrow', 'dateDropdownArrowV16');
  iconContainer.innerHTML = '&#9660;';
  iconContainer.setAttribute('data-row', String(row));
  iconContainer.setAttribute('data-col', String(column));
  iconContainer.style.position = 'absolute';
  iconContainer.style.right = '10px';
  iconContainer.style.top = '50%';
  iconContainer.style.transform = 'translateY(-50%)';
  iconContainer.style.cursor = 'pointer';
  iconContainer.style.fontSize = '11px';
  iconContainer.style.color = '#DFE3DE';

  td.style.position = 'relative';
  td.appendChild(dateContainer);
  td.appendChild(iconContainer);

  addDropdownIconMousedownHandlerV16({ hotInstance, row, column });
};

/**
 * DateEditorWrapperV16 - Custom date editor with Pikaday
 * Creates its own input field and Pikaday calendar
 */
export class DateEditorWrapperV16 extends TextEditor {
  static type = 'date-wrapper-v16';
  static dateFormatPattern = /^(?<day>[0-3]\d)\/(?<month>[0-1]\d)\/(?<year>\d{4})$/;

  private _initialValue?: string;
  private $datePicker: Pikaday | null = null;
  private dateInput: HTMLTextAreaElement | null = null;
  private datePickerContainer: HTMLDivElement | null = null;
  private outsideClickHandler: ((event: MouseEvent) => void) | null = null;
  private _isOpened = false;
  private _isDestroyed = false;
  private _selectedFromCalendar = false; // Track if date was selected from calendar picker
  private _rawTypedValue?: string; // Store raw typed value before Pikaday modifies it

  static register(type = DateEditorWrapperV16.type) {
    registerCellType(type, {
      editor: DateEditorWrapperV16 as unknown as typeof BaseEditor,
      renderer: dateRendererV16,
      validator: (value: unknown, callback: (valid: boolean) => void) =>
        callback(_isEmpty(value) || DateUtils.isValidDate(value as string)),
    });
  }

  init() {
    super.init();
    this.hot.addHook('afterDestroy', () => this.destroyElements());
  }

  /**
   * Override prepare to avoid v16 internal API issues
   * Do NOT call super.prepare() as it may call methods that don't exist in v16
   */
  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: CellProperties
  ) {
    this.row = row;
    this.col = col;
    this.prop = prop;
    this.TD = td;
    this.originalValue = originalValue;
    this.cellProperties = cellProperties;

    // Get current cell value - originalValue might be stale after previous edit
    const currentCellValue = this.hot?.getDataAtCell(row, col);
    this._initialValue = (currentCellValue as string) ?? (originalValue as string);
  }

  beginEditing(_initialValue?: string, _event?: Event) {
    this.open();
  }

  open() {
    if (_notNil(this.dateInput) === false) {
      this.createElements();
    }
    this._isOpened = true;
    this._selectedFromCalendar = false; // Reset flag on each open
    this._rawTypedValue = this._initialValue; // Initialize with current value
    this.showEditor();
  }

  createElements() {
    super.createElements();

    this.dateInput = document.createElement('textarea');
    this.dateInput.className = 'handsontableInput';
    this.dateInput.style.width = 'calc(100% + 2rem)';
    this.dateInput.style.height = 'calc(100% + 5px)';
    this.dateInput.style.border = 'none';
    this.dateInput.style.outline = 'none';
    this.dateInput.style.margin = '0 -1rem';
    this.dateInput.style.padding = '0 2px';
    this.dateInput.style.fontFamily = 'inherit';
    this.dateInput.style.fontSize = 'inherit';
    this.dateInput.style.top = '-2px';

    // Capture raw typed value on every input change
    this.dateInput.addEventListener('input', () => {
      if (_notNil(this.dateInput)) {
        this._rawTypedValue = this.dateInput.value;
        this._selectedFromCalendar = false; // User is typing, not selecting from calendar
      }
    });

    // Prevent Handsontable from intercepting keyboard events and re-rendering
    this.dateInput.addEventListener('keydown', (e) => {
      // console.log('keydown', this.originalValue, this.cellProperties);
      e.stopPropagation();
      // Handle Enter key to finish editing
      if (e.key === 'Enter') {
        e.preventDefault();
        this.finishEditing(false);
      }

      // Handle Escape key to cancel editing
      if (e.key === 'Escape') {
        e.preventDefault();
        this.finishEditing(true); // restore original value
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.setValue(DateUtils.formatDate(new Date()));
        this.finishEditing(false);
      }
    });
    this.dateInput.addEventListener('keyup', (e) => {
      e.stopPropagation();
    });
    this.dateInput.addEventListener('keypress', (e) => {
      e.stopPropagation();
    });

    // Create Pikaday container
    this.datePickerContainer = document.createElement('div');
    this.datePickerContainer.className = 'htDatepickerHolder';
    this.datePickerContainer.style.position = 'absolute';
    this.datePickerContainer.style.zIndex = '9999';
    document.body.appendChild(this.datePickerContainer);
  }

  showEditor() {
    if (_isNil(this.dateInput) || _isNil(this.TD)) {
      return;
    }

    this.dateInput.value = this._initialValue ?? '';

    this.TD.innerHTML = '';
    this.TD.appendChild(this.dateInput);

    this.dateInput.focus();
    this.dateInput.select();

    this.showDatePicker();
  }

  showDatePicker() {
    if (_isNil(this.dateInput) || _isNil(this.datePickerContainer) || _isNil(this.TD)) {
      return;
    }

    const tdRect = this.TD.getBoundingClientRect();
    this.datePickerContainer.style.top = `${tdRect.bottom + window.scrollY}px`;
    this.datePickerContainer.style.left = `${tdRect.left + window.scrollX}px`;
    this.datePickerContainer.style.display = 'block';

    if (_notNil(this.$datePicker)) {
      this.$datePicker.destroy();
    }

    const datePickerConfig =
      (this.cellProperties as CellProperties & { datePickerConfig?: Partial<Pikaday.PikadayOptions> })
        ?.datePickerConfig ?? {};

    this.$datePicker = new Pikaday({
      ...datePickerConfig,
      field: this.dateInput,
      bound: false,
      container: this.datePickerContainer,
      format: 'YYYY-MM-DD',
      parse: (dateString: string) => {
        if (DateUtils.isValidDate(dateString)) {
          return new Date(dateString);
        }
        return null;
      },
      toString: (date) => {
        if (date instanceof Date && !isNaN(date.getTime())) {
          return DateUtils.formatDate(date);
        }
        return '';
      },
      onSelect: (date) => {
        if (this._isDestroyed) {
          return;
        }
        this._selectedFromCalendar = true;
        const dateStr = DateUtils.formatDate(date);
        if (_notNil(this.dateInput)) {
          this.dateInput.value = dateStr;
          this._rawTypedValue = dateStr;
        }
        this.finishEditing(false);
      },
    });

    // Set initial date
    if (_notNil(this._initialValue) && DateUtils.isValidDate(this._initialValue)) {
      try {
        this.$datePicker.setDate(new Date(this._initialValue), true);
      } catch {
        // Invalid date
      }
    }

    this.$datePicker.show();

    // Add outside click handler
    this.outsideClickHandler = (event: MouseEvent) => {
      // Check if editor is still opened and instance is valid before processing
      if (!this._isOpened || this._isDestroyed) {
        return;
      }

      const target = event.target as Node;
      if (
        _notNil(this.datePickerContainer) &&
        this.datePickerContainer.contains(target) === false &&
        _notNil(this.dateInput) &&
        this.dateInput.contains(target) === false &&
        _notNil(this.TD) &&
        this.TD.contains(target) === false
      ) {
        this.finishEditing(false);
      }
    };

    setTimeout(() => {
      if (_notNil(this.outsideClickHandler) && !this._isDestroyed) {
        document.addEventListener('mousedown', this.outsideClickHandler, true);
      }
    }, 0);
  }

  close() {
    if (!this._isOpened) {
      return;
    }
    this._isOpened = false;

    if (_notNil(this.$datePicker)) {
      this.$datePicker.hide();
    }

    if (_notNil(this.datePickerContainer)) {
      this.datePickerContainer.style.display = 'none';
    }

    if (_notNil(this.outsideClickHandler)) {
      document.removeEventListener('mousedown', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }

    if (_notNil(this.dateInput) && _notNil(this.TD)) {
      if (this.TD.contains(this.dateInput)) {
        this.TD.removeChild(this.dateInput);
      }
    }

    if (_notNil(this.TD) && !this._isDestroyed) {
      try {
        this.hot.render();
      } catch {
        // Instance may have been destroyed, silently fail
      }
    }
  }

  focus() {
    if (_notNil(this.dateInput)) {
      this.dateInput.focus();
      this.dateInput.select();
    }
  }

  getValue(): string {
    return _notNil(this.dateInput) && _notNil(this.dateInput.value) ? this.dateInput.value : '';
  }

  setValue(newValue: string) {
    if (_notNil(this.dateInput)) {
      const value = _notNil(newValue) && newValue !== '' ? newValue : '';
      this.dateInput.value = value;
      // Also update raw typed value so programmatic setValue works correctly
      this._rawTypedValue = value;
    }
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false) {
    // Check if Handsontable instance is destroyed before any operations
    if (this._isDestroyed) {
      return;
    }

    if (!this._isOpened) {
      try {
        super.finishEditing(restoreOriginalValue, ctrlDown);
      } catch {
        // Instance may have been destroyed, silently fail
      }
      return;
    }

    // Capture value BEFORE close() which may trigger Pikaday modifications
    let valueToSave: string;
    if (this._selectedFromCalendar) {
      // User clicked on calendar - use the formatted date from input
      valueToSave = _notNil(this.dateInput) && _notNil(this.dateInput.value) ? this.dateInput.value : '';
    } else {
      // User typed manually - use raw typed value to preserve invalid input
      valueToSave = this._rawTypedValue ?? '';
    }

    this.close();

    // Re-check if Handsontable instance is still valid AFTER close()
    if (this._isDestroyed) {
      return;
    }

    try {
      if (restoreOriginalValue && _notNil(this._initialValue)) {
        this.hot.setDataAtCell(this.row, this.col, this._initialValue);
      } else {
        if (valueToSave !== this._initialValue) {
          this.hot.setDataAtCell(this.row, this.col, valueToSave);
        }
      }
    } catch {
      // Instance may have been destroyed during the operation
      // Silently fail to avoid console errors
    }

    // Final check before calling super
    if (this._isDestroyed) {
      return;
    }

    try {
      super.finishEditing(restoreOriginalValue, ctrlDown);
    } catch {
      // Instance may have been destroyed, silently fail
    }
  }

  isOpened() {
    return this._isOpened;
  }

  destroyElements() {
    this._isDestroyed = true;

    if (_notNil(this.$datePicker)) {
      this.$datePicker.destroy();
      this.$datePicker = null;
    }
    if (_notNil(this.datePickerContainer)) {
      this.datePickerContainer.remove();
      this.datePickerContainer = null;
    }
    if (_notNil(this.outsideClickHandler)) {
      document.removeEventListener('mousedown', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
    this.dateInput = null;
  }
}
